import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  CircularProgress,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BusinessIcon from '@mui/icons-material/Business';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import axios from 'axios';
import './index.css';
import {
  GET_REVENUE_DATA,
  UPDATE_REVENUE_DATA,
  DELETE_REVENUE_DATA,
} from '../../api/revenue.js';

const STATUS_OPTIONS = [
  'Pending',
  'Under Review',
  'Contacted',
  'Accepted',
  'Rejected',
];

const SECTOR_OPTIONS = [
  'B2B SaaS',
  'Fintech',
  'Consumer / D2C',
  'Enterprise Tech',
  'Healthcare / HealthTech',
  'EdTech',
  'Other',
];

const STAGE_OPTIONS = [
  'Pre-seed',
  'Seed',
  'Series A',
  'Series B+',
  'Bootstrapped',
];

const REVENUE_OPTIONS = [
  'Pre-revenue',
  '$1k - $10k',
  '$10k - $50k',
  '$50k - $100k',
  '$100k+',
];

const TEAM_OPTIONS = ['1-5', '6-15', '16-50', '50+'];

const TARGET_OPTIONS = [
  '₹25L - ₹50L',
  '₹50L - ₹1 Cr',
  '₹1 Cr - ₹3 Cr',
  '₹3 Cr - ₹5 Cr',
  '₹5 Cr - ₹10 Cr',
  '₹10 Cr+',
  '$100k - $250k',
  '$250k - $500k',
  '$500k - $1M',
  '$1M+',
];

const RevenueAdminView = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  // View modal state
  const [viewItem, setViewItem] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);

  // Edit modal state
  const [editItem, setEditItem] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');

  // Delete modal state
  const [deleteId, setDeleteId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(GET_REVENUE_DATA);
      if (res.data && Array.isArray(res.data.data)) {
        setData(res.data.data);
      } else if (Array.isArray(res.data)) {
        setData(res.data);
      } else {
        setData([]);
      }
    } catch (err) {
      console.error('Error fetching revenue submissions:', err);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filtered list
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const matchesSearch =
        searchTerm === '' ||
        (item.fullName &&
          item.fullName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.companyName &&
          item.companyName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.workEmail &&
          item.workEmail.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.sector &&
          item.sector.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.cityCountry &&
          item.cityCountry.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesStatus =
        statusFilter === 'All' || item.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [data, searchTerm, statusFilter]);

  // Statistics
  const stats = useMemo(() => {
    const total = data.length;
    const pending = data.filter((d) => d.status === 'Pending' || !d.status).length;
    const underReview = data.filter((d) => d.status === 'Under Review').length;
    const accepted = data.filter((d) => d.status === 'Accepted').length;
    return { total, pending, underReview, accepted };
  }, [data]);

  // Handlers for View
  const handleOpenView = (item) => {
    setViewItem(item);
    setViewOpen(true);
  };

  const handleCloseView = () => {
    setViewOpen(false);
    setViewItem(null);
  };

  // Handlers for Edit
  const handleOpenEdit = (item) => {
    setEditItem({
      _id: item._id,
      fullName: item.fullName || '',
      workEmail: item.workEmail || '',
      companyName: item.companyName || '',
      website: item.website || '',
      cityCountry: item.cityCountry || '',
      phone: item.phone || '',
      sector: item.sector || '',
      stage: item.stage || '',
      monthlyRevenue: item.monthlyRevenue || '',
      teamSize: item.teamSize || '',
      marketsServed: item.marketsServed || '',
      growthPriority: item.growthPriority || '',
      incrementalTarget: item.incrementalTarget || '',
      ninetyDaysGoal: item.ninetyDaysGoal || '',
      urgencyReason: item.urgencyReason || '',
      status: item.status || 'Pending',
      adminNotes: item.adminNotes || '',
    });
    setEditError('');
    setEditOpen(true);
  };

  const handleCloseEdit = () => {
    setEditOpen(false);
    setEditItem(null);
    setEditError('');
  };

  const handleEditChange = (field, value) => {
    setEditItem((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveEdit = async () => {
    if (!editItem || !editItem._id) return;
    setEditLoading(true);
    setEditError('');

    try {
      const updateUrl = `${UPDATE_REVENUE_DATA}/${editItem._id}`;
      const payload = {
        fullName: editItem.fullName,
        workEmail: editItem.workEmail,
        companyName: editItem.companyName,
        website: editItem.website,
        cityCountry: editItem.cityCountry,
        phone: editItem.phone,
        sector: editItem.sector,
        stage: editItem.stage,
        monthlyRevenue: editItem.monthlyRevenue,
        teamSize: editItem.teamSize,
        marketsServed: editItem.marketsServed,
        growthPriority: editItem.growthPriority,
        incrementalTarget: editItem.incrementalTarget,
        ninetyDaysGoal: editItem.ninetyDaysGoal,
        urgencyReason: editItem.urgencyReason,
        status: editItem.status,
        adminNotes: editItem.adminNotes,
      };

      const res = await axios.put(updateUrl, payload);
      const updated = res.data.data;

      setData((prev) =>
        prev.map((item) => (item._id === updated._id ? updated : item))
      );

      // Also update viewItem if open
      if (viewItem && viewItem._id === updated._id) {
        setViewItem(updated);
      }

      handleCloseEdit();
    } catch (err) {
      console.error('Failed to update revenue submission:', err);
      if (err.response && err.response.data) {
        const details = err.response.data.details;
        setEditError(
          Array.isArray(details) ? details.join(', ') : err.response.data.error || 'Failed to update'
        );
      } else {
        setEditError('Network error while saving changes.');
      }
    } finally {
      setEditLoading(false);
    }
  };

  // Handlers for Delete
  const handleOpenDelete = (id) => {
    setDeleteId(id);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${DELETE_REVENUE_DATA}/${deleteId}`);
      setData((prev) => prev.filter((item) => item._id !== deleteId));
      setDeleteOpen(false);
      setDeleteId(null);
    } catch (err) {
      console.error('Failed to delete submission:', err);
      alert('Failed to delete submission.');
    }
  };

  // Export to CSV
  const handleExportCSV = () => {
    if (!filteredData.length) return;
    const headers = [
      'Company Name',
      'Founder Name',
      'Work Email',
      'Phone',
      'City & Country',
      'Sector',
      'Stage',
      'Monthly Revenue',
      'Team Size',
      'Growth Priority',
      '12M Target',
      'Status',
      'Date Submitted',
    ];

    const rows = filteredData.map((d) => [
      `"${(d.companyName || '').replace(/"/g, '""')}"`,
      `"${(d.fullName || '').replace(/"/g, '""')}"`,
      `"${(d.workEmail || '').replace(/"/g, '""')}"`,
      `"${(d.phone || '').replace(/"/g, '""')}"`,
      `"${(d.cityCountry || '').replace(/"/g, '""')}"`,
      `"${(d.sector || '').replace(/"/g, '""')}"`,
      `"${(d.stage || '').replace(/"/g, '""')}"`,
      `"${(d.monthlyRevenue || '').replace(/"/g, '""')}"`,
      `"${(d.teamSize || '').replace(/"/g, '""')}"`,
      `"${(d.growthPriority || '').replace(/"/g, '""')}"`,
      `"${(d.incrementalTarget || '').replace(/"/g, '""')}"`,
      `"${(d.status || 'Pending').replace(/"/g, '""')}"`,
      `"${d.createdAt ? new Date(d.createdAt).toLocaleDateString() : ''}"`,
    ]);

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `revenue_exchange_submissions_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusChipClass = (status) => {
    switch (status) {
      case 'Accepted':
        return 'chip-accepted';
      case 'Under Review':
        return 'chip-review';
      case 'Contacted':
        return 'chip-contacted';
      case 'Rejected':
        return 'chip-rejected';
      case 'Pending':
      default:
        return 'chip-pending';
    }
  };

  return (
    <Box className="revenue-admin-wrapper">
      {/* Top Banner & Header */}
      <Box className="revenue-admin-header">
        <div>
          <Typography variant="h4" className="revenue-page-title">
            Revenue Exchange Leads
          </Typography>
          <Typography variant="body2" className="revenue-page-subtitle">
            Manage, review, and collaborate on operator-backed growth briefs
          </Typography>
        </div>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
            className="revenue-btn-refresh"
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportCSV}
            className="revenue-btn-export"
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      {/* Metrics Row */}
      <Grid container spacing={2.5} className="revenue-stats-grid">
        <Grid item xs={12} sm={6} md={3}>
          <div className="revenue-stat-card">
            <div className="revenue-stat-icon stat-icon-total">
              <BusinessIcon />
            </div>
            <div>
              <div className="revenue-stat-value">{stats.total}</div>
              <div className="revenue-stat-label">Total Applications</div>
            </div>
          </div>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <div className="revenue-stat-card">
            <div className="revenue-stat-icon stat-icon-pending">
              <HourglassEmptyIcon />
            </div>
            <div>
              <div className="revenue-stat-value">{stats.pending}</div>
              <div className="revenue-stat-label">Pending Review</div>
            </div>
          </div>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <div className="revenue-stat-card">
            <div className="revenue-stat-icon stat-icon-review">
              <TrendingUpIcon />
            </div>
            <div>
              <div className="revenue-stat-value">{stats.underReview}</div>
              <div className="revenue-stat-label">Under Active Review</div>
            </div>
          </div>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <div className="revenue-stat-card">
            <div className="revenue-stat-icon stat-icon-accepted">
              <CheckCircleOutlineIcon />
            </div>
            <div>
              <div className="revenue-stat-value">{stats.accepted}</div>
              <div className="revenue-stat-label">Accepted Fit</div>
            </div>
          </div>
        </Grid>
      </Grid>

      {/* Filter and Search Bar */}
      <Box className="revenue-filter-bar">
        <TextField
          size="small"
          placeholder="Search by company, founder, email, sector..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="revenue-search-input"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#888' }} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          select
          size="small"
          label="Filter by Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ minWidth: 170 }}
        >
          <MenuItem value="All">All Statuses ({data.length})</MenuItem>
          {STATUS_OPTIONS.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt} ({data.filter((d) => (d.status || 'Pending') === opt).length})
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {/* Table Container */}
      <TableContainer component={Paper} className="revenue-table-paper">
        <Table>
          <TableHead className="revenue-table-head">
            <TableRow>
              <TableCell className="revenue-head-cell">Company & Founder</TableCell>
              <TableCell className="revenue-head-cell">Sector & Stage</TableCell>
              <TableCell className="revenue-head-cell">Revenue / 12M Target</TableCell>
              <TableCell className="revenue-head-cell">Growth Priority</TableCell>
              <TableCell className="revenue-head-cell">Status</TableCell>
              <TableCell className="revenue-head-cell">Date</TableCell>
              <TableCell className="revenue-head-cell" align="center">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={36} sx={{ color: '#7c65c1' }} />
                  <Typography variant="body2" sx={{ mt: 1.5, color: '#666' }}>
                    Loading revenue exchange briefs...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <Typography variant="body1" sx={{ color: '#555', fontWeight: 600 }}>
                    No revenue applications found
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#888', mt: 0.5 }}>
                    {searchTerm || statusFilter !== 'All'
                      ? 'Try adjusting your search query or filters'
                      : 'New applications will appear here as founders apply.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredData.map((row) => (
                <TableRow key={row._id} className="revenue-table-row">
                  <TableCell>
                    <div className="company-col-title">{row.companyName || 'N/A'}</div>
                    <div className="company-col-founder">
                      {row.fullName} • <span className="text-email">{row.workEmail}</span>
                    </div>
                    {row.cityCountry && (
                      <div className="company-col-location">📍 {row.cityCountry}</div>
                    )}
                  </TableCell>

                  <TableCell>
                    <div className="badge-sector">{row.sector}</div>
                    <div className="text-stage">{row.stage}</div>
                  </TableCell>

                  <TableCell>
                    <div className="text-revenue-val">
                      <strong>Rev:</strong> {row.monthlyRevenue}
                    </div>
                    <div className="text-target-val">
                      <strong>Target:</strong> {row.incrementalTarget}
                    </div>
                  </TableCell>

                  <TableCell>
                    <div className="text-growth-priority">
                      {row.growthPriority}
                    </div>
                    {row.selectedSupport && row.selectedSupport.length > 0 && (
                      <div className="text-support-count">
                        +{row.selectedSupport.length} support areas
                      </div>
                    )}
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={row.status || 'Pending'}
                      size="small"
                      className={`status-chip ${getStatusChipClass(row.status || 'Pending')}`}
                    />
                  </TableCell>

                  <TableCell>
                    <span className="text-date">
                      {row.createdAt
                        ? new Date(row.createdAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })
                        : '—'}
                    </span>
                  </TableCell>

                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                      <Tooltip title="View Full Brief">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenView(row)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Details & Status">
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => handleOpenEdit(row)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleOpenDelete(row._id)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* =========================================================================
         VIEW DETAILS MODAL
         ========================================================================= */}
      <Dialog
        open={viewOpen}
        onClose={handleCloseView}
        maxWidth="md"
        fullWidth
        PaperProps={{ className: 'revenue-dialog-paper' }}
      >
        {viewItem && (
          <>
            <DialogTitle className="revenue-dialog-title">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Typography variant="h5" component="div" sx={{ fontWeight: 700 }}>
                    {viewItem.companyName}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#888' }}>
                    Submitted by {viewItem.fullName} on{' '}
                    {viewItem.createdAt
                      ? new Date(viewItem.createdAt).toLocaleString()
                      : 'N/A'}
                  </Typography>
                </div>
                <Chip
                  label={viewItem.status || 'Pending'}
                  className={`status-chip ${getStatusChipClass(viewItem.status || 'Pending')}`}
                />
              </Box>
            </DialogTitle>

            <DialogContent dividers className="revenue-dialog-content">
              {/* Founder & Contact Details */}
              <div className="view-section-header">1. Company & Founder Overview</div>
              <Grid container spacing={2} sx={{ mb: 2.5 }}>
                <Grid item xs={12} sm={6}>
                  <div className="view-detail-box">
                    <span className="view-detail-label">Founder Name</span>
                    <span className="view-detail-val">{viewItem.fullName}</span>
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className="view-detail-box">
                    <span className="view-detail-label">Work Email</span>
                    <span className="view-detail-val">{viewItem.workEmail}</span>
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className="view-detail-box">
                    <span className="view-detail-label">Website</span>
                    <span className="view-detail-val">
                      {viewItem.website ? (
                        <a
                          href={
                            viewItem.website.startsWith('http')
                              ? viewItem.website
                              : `https://${viewItem.website}`
                          }
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: '#7c65c1', textDecoration: 'underline' }}
                        >
                          {viewItem.website}
                        </a>
                      ) : (
                        'Not provided'
                      )}
                    </span>
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className="view-detail-box">
                    <span className="view-detail-label">Phone & Location</span>
                    <span className="view-detail-val">
                      {viewItem.phone || 'No phone'} ({viewItem.cityCountry})
                    </span>
                  </div>
                </Grid>
              </Grid>

              {/* Traction & Scale */}
              <div className="view-section-header">2. Traction & Current State</div>
              <Grid container spacing={2} sx={{ mb: 2.5 }}>
                <Grid item xs={6} sm={3}>
                  <div className="view-detail-box">
                    <span className="view-detail-label">Sector</span>
                    <span className="view-detail-val">{viewItem.sector}</span>
                  </div>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <div className="view-detail-box">
                    <span className="view-detail-label">Stage</span>
                    <span className="view-detail-val">{viewItem.stage}</span>
                  </div>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <div className="view-detail-box">
                    <span className="view-detail-label">Monthly Revenue</span>
                    <span className="view-detail-val">{viewItem.monthlyRevenue}</span>
                  </div>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <div className="view-detail-box">
                    <span className="view-detail-label">Team Size</span>
                    <span className="view-detail-val">{viewItem.teamSize}</span>
                  </div>
                </Grid>
                {viewItem.marketsServed && (
                  <Grid item xs={12}>
                    <div className="view-detail-box">
                      <span className="view-detail-label">Markets Currently Served</span>
                      <span className="view-detail-val">{viewItem.marketsServed}</span>
                    </div>
                  </Grid>
                )}
              </Grid>

              {/* Growth Brief */}
              <div className="view-section-header">3. Growth Brief & Targets</div>
              <Grid container spacing={2} sx={{ mb: 2.5 }}>
                <Grid item xs={12} sm={6}>
                  <div className="view-detail-box">
                    <span className="view-detail-label">Growth Priority</span>
                    <span className="view-detail-val highlight-target">
                      {viewItem.growthPriority}
                    </span>
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className="view-detail-box">
                    <span className="view-detail-label">12-Month Incremental Target</span>
                    <span className="view-detail-val highlight-target">
                      {viewItem.incrementalTarget}
                    </span>
                  </div>
                </Grid>
                <Grid item xs={12}>
                  <div className="view-detail-box">
                    <span className="view-detail-label">What must be true 90 days from now?</span>
                    <div className="view-detail-narrative">{viewItem.ninetyDaysGoal}</div>
                  </div>
                </Grid>
              </Grid>

              {/* Partnership Fit */}
              <div className="view-section-header">4. Partnership Fit & Urgency</div>
              <Box sx={{ mb: 2.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#444', mb: 1 }}>
                  Support Needed:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {viewItem.selectedSupport && viewItem.selectedSupport.length > 0 ? (
                    viewItem.selectedSupport.map((opt) => (
                      <Chip key={opt} label={opt} className="view-support-pill" />
                    ))
                  ) : (
                    <Typography variant="caption" sx={{ color: '#888' }}>
                      None selected
                    </Typography>
                  )}
                </Box>

                <div className="view-detail-box">
                  <span className="view-detail-label">Why is now the right time?</span>
                  <div className="view-detail-narrative">{viewItem.urgencyReason}</div>
                </div>
              </Box>

              {/* Admin Notes */}
              {viewItem.adminNotes && (
                <Box sx={{ mb: 1 }}>
                  <div className="view-section-header">Operator / Admin Notes</div>
                  <div className="view-admin-notes-card">{viewItem.adminNotes}</div>
                </Box>
              )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button
                onClick={() => {
                  handleCloseView();
                  handleOpenEdit(viewItem);
                }}
                variant="outlined"
                startIcon={<EditIcon />}
              >
                Edit Brief
              </Button>
              <Button onClick={handleCloseView} variant="contained" className="revenue-btn-export">
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* =========================================================================
         EDIT MODAL
         ========================================================================= */}
      <Dialog
        open={editOpen}
        onClose={handleCloseEdit}
        maxWidth="md"
        fullWidth
        PaperProps={{ className: 'revenue-dialog-paper' }}
      >
        {editItem && (
          <>
            <DialogTitle className="revenue-dialog-title">
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Edit Application: {editItem.companyName}
              </Typography>
              <Typography variant="caption" sx={{ color: '#888' }}>
                Update details, status, and internal operator notes
              </Typography>
            </DialogTitle>

            <DialogContent dividers className="revenue-dialog-content">
              {editError && (
                <div className="revenue-edit-error-banner">
                  ⚠️ {editError}
                </div>
              )}

              {/* Status and Notes */}
              <div className="view-section-header">Status & Internal Management</div>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Application Status"
                    value={editItem.status}
                    onChange={(e) => handleEditChange('status', e.target.value)}
                  >
                    {STATUS_OPTIONS.map((st) => (
                      <MenuItem key={st} value={st}>
                        {st}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Operator Notes / Next Actions"
                    placeholder="e.g. Intro call scheduled with Raj; fit confirmed on enterprise sales"
                    value={editItem.adminNotes}
                    onChange={(e) => handleEditChange('adminNotes', e.target.value)}
                  />
                </Grid>
              </Grid>

              {/* Company and Founder */}
              <div className="view-section-header">Company & Founder Information</div>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Company Name"
                    value={editItem.companyName}
                    onChange={(e) => handleEditChange('companyName', e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Founder / Lead Name"
                    value={editItem.fullName}
                    onChange={(e) => handleEditChange('fullName', e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="email"
                    label="Work Email"
                    value={editItem.workEmail}
                    onChange={(e) => handleEditChange('workEmail', e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Website"
                    value={editItem.website}
                    onChange={(e) => handleEditChange('website', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="City & Country"
                    value={editItem.cityCountry}
                    onChange={(e) => handleEditChange('cityCountry', e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Phone"
                    value={editItem.phone}
                    onChange={(e) => handleEditChange('phone', e.target.value)}
                  />
                </Grid>
              </Grid>

              {/* Traction & Scale */}
              <div className="view-section-header">Traction & Metrics</div>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={3}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Sector"
                    value={editItem.sector}
                    onChange={(e) => handleEditChange('sector', e.target.value)}
                  >
                    {SECTOR_OPTIONS.map((sec) => (
                      <MenuItem key={sec} value={sec}>
                        {sec}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Stage"
                    value={editItem.stage}
                    onChange={(e) => handleEditChange('stage', e.target.value)}
                  >
                    {STAGE_OPTIONS.map((stg) => (
                      <MenuItem key={stg} value={stg}>
                        {stg}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Monthly Revenue"
                    value={editItem.monthlyRevenue}
                    onChange={(e) => handleEditChange('monthlyRevenue', e.target.value)}
                  >
                    {REVENUE_OPTIONS.map((rev) => (
                      <MenuItem key={rev} value={rev}>
                        {rev}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Team Size"
                    value={editItem.teamSize}
                    onChange={(e) => handleEditChange('teamSize', e.target.value)}
                  >
                    {TEAM_OPTIONS.map((tm) => (
                      <MenuItem key={tm} value={tm}>
                        {tm}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Markets Served"
                    value={editItem.marketsServed}
                    onChange={(e) => handleEditChange('marketsServed', e.target.value)}
                  />
                </Grid>
              </Grid>

              {/* Growth Brief & Goals */}
              <div className="view-section-header">Growth Brief & Target</div>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Growth Priority"
                    value={editItem.growthPriority}
                    onChange={(e) => handleEditChange('growthPriority', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="12-Month Target"
                    value={editItem.incrementalTarget}
                    onChange={(e) => handleEditChange('incrementalTarget', e.target.value)}
                  >
                    {TARGET_OPTIONS.map((t) => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    size="small"
                    label="90-Day Goal"
                    value={editItem.ninetyDaysGoal}
                    onChange={(e) => handleEditChange('ninetyDaysGoal', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                    label="Urgency Reason"
                    value={editItem.urgencyReason}
                    onChange={(e) => handleEditChange('urgencyReason', e.target.value)}
                  />
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={handleCloseEdit} disabled={editLoading}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveEdit}
                variant="contained"
                className="revenue-btn-export"
                disabled={editLoading}
              >
                {editLoading ? <CircularProgress size={20} color="inherit" /> : 'Save Changes'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* =========================================================================
         DELETE CONFIRMATION MODAL
         ========================================================================= */}
      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary">
            Are you sure you want to permanently remove this revenue brief? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RevenueAdminView;
